import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import MotifSearch from '../../../Common/Search/MotifSearch';
import CheckboxItem from './CheckboxItem';
import {labels} from '../../../../util/utils';
import Utility from '../../../../util/utilityfunctions';
import Button from '../../../CoreWrappers/Button';

const FiltersList = (props) => {
	const minLevel = 2;
	const {dashboardEntities, onCheckItem, onCheckAll, isGrouping, allEntities, level} = props;
	const [selectedAll, setSelectedAll] = useState(false);
	const [entities, setEntities] = useState([]);
	const [noResult, setNoResult] = useState(false);
	const [groupedEntities, setGroupedEntities] = useState(null);
	const [isSearching, setIsSearching] = useState(false);
	const [searchValue, setSearchValue] = useState('');

	useEffect(() => {
		if (dashboardEntities?.length > 0 && !isSearching) {
			if (dashboardEntities[0]?.entityLevel > 2) {
				groupEntitiesByParent(dashboardEntities);
			}
			setEntities(_.sortBy(dashboardEntities, 'label'));
			setSelectedAll(isEachEntitySelected);
		}
	}, [dashboardEntities]);

	const areValues = () => {
		return dashboardEntities?.length > 0;
	};

	const isEachEntitySelected = areValues()
		? dashboardEntities.filter(({checked}) => checked).length === dashboardEntities.length
		: false;

	const groupEntitiesByParent = (ent) => {
		const groupEntities = _.groupBy(ent, (entity) => entity.parentId);

		const groups = Object.keys(groupEntities).map((key) => {
			const parent = allEntities.find((e) => e.id === key);
			return {
				id: parent.id,
				label: parent.label,
				children: _.sortBy([...groupEntities[key]], 'label')
			};
		});

		setGroupedEntities(_.sortBy(groups, 'label'));
	};

	const handlerOnItemCheck = (value, isChecked) => {
		if (onCheckItem) {
			onCheckItem(value, isChecked);
		}
		setSelectedAll(isEachEntitySelected);
	};

	const handlerOnSelectAll = (selected) => {
		const selectedEntities = [
			...entities.map((item) => {
				return {...item, checked: !selected};
			})
		];

		setEntities(selectedEntities);
		setSelectedAll(isEachEntitySelected);

		if (onCheckAll) {
			onCheckAll(selectedEntities, !selected);
		}
	};

	const handlerOnSearch = (event) => {
		const searchList = [];
		setSearchValue(event.target.value);
		setNoResult(false);

		if (event.target.value.length > 0 && entities) {
			setIsSearching(true);
			dashboardEntities.forEach((entity) => {
				if (Utility.isTextIncluded(entity.label, event.target.value)) {
					searchList.push(entity);
				}
			});

			setNoResult(searchList.length === 0);
			setEntities(_.sortBy(searchList, 'label'));
			if (groupedEntities) {
				groupEntitiesByParent(searchList);
			}
		} else {
			setIsSearching(false);
			setEntities(_.sortBy(dashboardEntities, 'label'));
			if (dashboardEntities[0]?.entityLevel > minLevel) {
				groupEntitiesByParent(dashboardEntities);
			}
			setSelectedAll(isEachEntitySelected);
		}
	};

	const handlerOnClear = () => {
		setNoResult(false);
		setIsSearching(false);
		setEntities(_.sortBy(dashboardEntities, 'label'));
		if (dashboardEntities[0]?.entityLevel > minLevel) {
			groupEntitiesByParent(dashboardEntities);
		}
		setSelectedAll(isEachEntitySelected);
	};

	return (
		<StyledFiltersList className={`level-${level}`} length={dashboardEntities.length}>
			{dashboardEntities.length === 0 && <div className="no-data">{labels.thereIsNoDataToDisplay}</div>}
			{dashboardEntities.length > 0 && (
				<>
					<section className="listitem">
						<Button
							variant={'text-alt'}
							label={selectedAll ? labels.deselectAll : labels.selectAll}
							onClick={() => handlerOnSelectAll(selectedAll)}
							disabled={isGrouping ? !groupedEntities?.length : !entities?.length}
						/>
					</section>
					<MotifSearch
						value={searchValue}
						onChange={handlerOnSearch}
						onClear={handlerOnClear}
						placeholder={labels.search}
						searchHoverText={labels.search}
					/>
				</>
			)}
			<ul>
				{isGrouping &&
					groupedEntities &&
					groupedEntities.map((group) => {
						return (
							<li className="group-list">
								<div className="group-header ellipsis-one-line" title={group.label}>
									{group.label}
								</div>
								<div>
									{group.children.map((eg) => {
										return <CheckboxItem key={eg.id} entity={eg} onCheckChange={handlerOnItemCheck} />;
									})}
								</div>
							</li>
						);
					})}
				{!isGrouping &&
					entities &&
					entities.length > 0 &&
					entities.map((entity) => {
						return (
							<li>
								<CheckboxItem key={entity.id} entity={entity} onCheckChange={handlerOnItemCheck} />
							</li>
						);
					})}

				{noResult && <div className="no-results-found">{labels.noResultsFound}</div>}
			</ul>
		</StyledFiltersList>
	);
};

FiltersList.propTypes = {
	dashboardEntities: PropTypes.arrayOf(
		PropTypes.shape({
			name: PropTypes.string,
			count: PropTypes.number,
			level: PropTypes.number
		})
	),
	isGrouping: PropTypes.bool,
	onCheckItem: PropTypes.func,
	onCheckAll: PropTypes.func
};

export default FiltersList;

const StyledFiltersList = styled.section`
	display: flex;
	flex-direction: column;
	flex: 1;
	padding: 0.5rem 2rem;
	font-size: 1.3rem;
	border-right: 0.1rem solid ${(props) => props.theme.color[`grey300`]};
	ul {
		margin: 1rem 0;
	}
	.listitem {
		padding-bottom: 1rem;
		height: 3rem;
	}
	p {
		display: inline;
		padding-left: 1rem;
	}
	.motif-checkbox-label {
		display: flex;
		flex-direction: row;

		span {
			margin: 0 1rem;
		}
	}
	.searchControl {
		max-height: 3rem;
		input {
			font-size: 1.3rem;
		}
	}
	h5 {
		display: inline;
	}
	.no-results-found {
		margin: 2rem auto;
		text-align: center;
		font-size: 1.3rem;
	}
	.searchControl input {
		font-size: 1.3rem;
	}
	.group-header {
		font-weight: bold;
		padding-top: 1rem;
		font-size: 1.4rem;
	}
	.no-data {
		margin: 8rem auto;
		font-weight: 500;
		font-size: 1.3rem;
	}
	.group-list {
		padding-bottom: 0.8rem;
	}
`;
